import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import type { ConversationStore } from '@/features/messages/ConversationStore'
import type { Message } from '@/features/messages/Message'
import { action, computed } from 'mobx'

/**
 * View Model for a message in a conversation.
 */
export class ConversationMessageViewModel {
  private readonly _lifecycle = new LifecycleTracker()

  constructor(
    private readonly _conversationStore: ConversationStore,
    private readonly _isUserPayingAttention: () => boolean,
    public readonly message: Message,
  ) {}

  /**
   * Unique key to use for the message.
   */
  @computed get key() {
    return this.message.cid ?? this.message.id
  }

  /**
   * Whether this message can be marked as read by the current user.
   */
  @computed get canMarkAsRead() {
    if (!this._isUserPayingAttention()) {
      return false
    }

    return this.message.isUnread
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Attempts to send the message again.
   */
  @action.bound
  retrySend() {
    void this._conversationStore.sendMessage(this.message)
  }

  /**
   * Marks the message as read.
   */
  @action.bound
  markRead() {
    this._conversationStore.markAsRead(this.message)
  }
}
